/**
 * VisualizationRenderer - Central component for rendering all visualization types in chat mode.
 *
 * This component acts as a router, receiving structured visualization data from the backend
 * and rendering the appropriate visualization component (discovery results, network, knockdown, etc.).
 */

import React from 'react';
import { NetworkGraphViz } from './NetworkGraphViz';
import { KnockdownResultsViz } from './KnockdownResultsViz';
import { PathwayEnrichmentViz } from './PathwayEnrichmentViz';
import { TargetComparisonViz } from './TargetComparisonViz';
import { ASOResultsViz } from './ASOResultsViz';
import { LncRNAProfileViz } from './LncRNAProfileViz';
import { DiseaseTargetsDashboard } from './DiseaseTargetsDashboard';

export interface VisualizationData {
  type: 'discovery_results' | 'network' | 'knockdown' | 'pathway_enrichment' | 'comparison' | 'literature_network' | 'aso' | 'lncrna_profile' | 'disease_dashboard';
  interactive?: boolean;
  [key: string]: any; // Allow all visualization-specific fields
}

interface VisualizationRendererProps {
  visualization: VisualizationData;
  onAction?: (action: string, params?: any) => void;
}

export const VisualizationRenderer: React.FC<VisualizationRendererProps> = ({
  visualization,
  onAction
}) => {
  if (!visualization || !visualization.type) {
    return null;
  }

  const handleAction = (action: string, params?: any) => {
    if (onAction) {
      onAction(action, params);
    }
  };

  switch (visualization.type) {
    case 'discovery_results':
      // Target tables should not have visualizations - only text results
      return null;

    case 'network':
      return (
        <NetworkGraphViz
          data={visualization as any}
          interactive={visualization.interactive !== false}
          onAction={handleAction}
        />
      );

    case 'knockdown':
      return (
        <KnockdownResultsViz
          data={visualization as any}
          onAction={handleAction}
        />
      );

    case 'aso':
      return (
        <ASOResultsViz
          data={visualization as any}
          onAction={handleAction}
        />
      );

    case 'pathway_enrichment':
      // Skip if no valid pathway data
      const pathways = (visualization as any)?.pathways || [];
      // Check if pathways exist AND are valid (validation: fold_enrichment > 0 && fdr > 0 && fdr < 1)
      const validPathways = pathways.filter(
        (p: any) => p.fold_enrichment > 0 && p.fdr > 0 && p.fdr < 1
      );
      if (validPathways.length === 0) {
        return null;  // Don't show error box for empty or invalid pathways
      }
      return (
        <PathwayEnrichmentViz
          data={visualization as any}
          onAction={handleAction}
        />
      );

    case 'comparison':
      return (
        <TargetComparisonViz
          data={visualization as any}
          onAction={handleAction}
        />
      );

    case 'lncrna_profile':
      return (
        <LncRNAProfileViz
          data={visualization as any}
          onAction={handleAction}
        />
      );

    case 'disease_dashboard':
      return (
        <DiseaseTargetsDashboard
          data={visualization as any}
          onAction={handleAction}
        />
      );

    case 'literature_network':
      // TODO: Implement in Phase 3
      return (
        <div className="origin-card p-4 mt-4">
          <p className="text-sm text-gray-400">Literature network visualization coming soon...</p>
        </div>
      );

    default:
      console.warn(`Unknown visualization type: ${visualization.type}`);
      return null;
  }
};

export default VisualizationRenderer;
